import React, { FunctionComponent, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useBreakpointMatch } from "../../hooks/useBreakpointMatch";
import { useForm } from "../../hooks/useForm";
import { useLogFeatureUsage } from "../../queries/useLogFeatureUsage";
import { useIsLoggedOn } from "../../stores/hooks/useIsLoggedOn";
import { useNullableDealerAccountInfo } from "../../stores/hooks/useNullableDealerAccountInfo";
import { Button, RoundButton } from "../../ui/Button/Button";
import { Dropdown } from "../../ui/Dropdown";
import { Input } from "../../ui/Input/Input";
import { TabButton } from "../../ui/Tabs/TabButton";
import { TabsGroup } from "../../ui/Tabs/TabsGroup";
import { Toggle } from "../../ui/Toggle";
import { CalculatorIcon } from "../../ui/icons/CalculatorIcon";
import { CalendarIcon } from "../../ui/icons/CalendarIcon";
import { CheckIcon } from "../../ui/icons/CheckIcon";
import { DollarIcon } from "../../ui/icons/DollarIcon";
import { PercentIcon } from "../../ui/icons/PercentIcon";
import { calculateAmmortizingLoan } from "./calculateAmmortizingLoan";

export const states = ["bWeekly", "monthly"] as const;

const schema = z.object({
  amount: z
    .number({
      coerce: true,
      invalid_type_error: "calculator.error.positiveNumber",
    })
    .min(0, { message: "calculator.error.positiveNumber" })
    .optional(),
  interest: z
    .number({
      coerce: true,
      invalid_type_error: "calculator.error.positiveNumber",
    })
    .min(0, { message: "calculator.error.positiveNumber" })
    .optional(),
  term: z
    .number({
      coerce: true,
      invalid_type_error: "calculator.error.positiveInteger",
    })
    .min(0, { message: "calculator.error.positiveNumber" })
    .int({ message: "calculator.error.positiveInteger" })
    .optional(),
});

export type Form = z.infer<typeof schema>;

type CalculatorProps = {
  variant?: "inline";
};

const UncachedCalculator: FunctionComponent<CalculatorProps> = ({
  variant,
}) => {
  const dealer = useNullableDealerAccountInfo();
  const { t } = useTranslation();
  const [state, setState] = useState<(typeof states)[number]>(states[0]);
  const [form, setForm] = useState<Form>({});
  const isLoggedIn = useIsLoggedOn();
  const [handleChange] = useForm({
    liveErrors: true,
    noReset: true,
    onSubmit: setForm,
    schema,
  });

  const isMd = useBreakpointMatch("md");
  const logFeatureUsage = useLogFeatureUsage();

  if (!isLoggedIn || !Number(dealer?.dealerEnablePaymentCalculator)) {
    return null;
  }
  return (
    <Toggle
      onToggle={(isOn): void => {
        if (isOn) {
          logFeatureUsage("paymentCalculator");
        }
      }}
    >
      {variant === "inline" ? (
        <Button
          aria-label="showCalclator"
          className="font-thin text-ink-2 px-2 md:px-4"
        >
          <CalculatorIcon className="h-6 w-auto" />
          <span className="hidden md:inline">{t("leadForm.calculator")}</span>
        </Button>
      ) : (
        <RoundButton
          aria-label="showCalclator"
          className="tw-calculator__trigger"
          id="calculator-button"
        >
          <CalculatorIcon className="tw-calculator__icon" />
        </RoundButton>
      )}
      <Dropdown
        aria-labelledby="calculator-button"
        className="tw-calculator__dropdown"
        id="calculator-dropdown"
        offset={16}
        placement={isMd ? "bottom-right" : "bottom"}
        role="dialog"
      >
        <form
          className="tw-calculator__form"
          noValidate
          onBlur={handleChange}
          onSubmit={handleChange}
        >
          <TabsGroup id="calculator-tabs">
            {states.map((s) => (
              <TabButton
                onClick={(): void => {
                  setState(s);
                }}
                aria-label="calculatorMode"
                key={s}
                selected={s === state}
              >
                {t(`calculator.${s}`)}
              </TabButton>
            ))}
          </TabsGroup>
          <Input
            after={
              Boolean(form.amount) && (
                <span className="tw-calculator__input-after">$</span>
              )
            }
            aria-label="financeAmount"
            autoComplete="off"
            before={<CheckIcon className="tw-calculator__icon" />}
            inputMode="numeric"
            name="amount"
            placeholder={t("calculator.placeHolderAmount")}
          />
          <Input
            after={
              Boolean(form.term) && (
                <span className="tw-calculator__input-after">
                  {t(
                    state === "bWeekly"
                      ? "calculator.week"
                      : "calculator.month",
                    { count: form.term ?? 0 },
                  )}
                </span>
              )
            }
            placeholder={t(
              state === "bWeekly"
                ? "calculator.placeholderWeeks"
                : "calculator.placeholderMonths",
            )}
            aria-label="financeTerm"
            autoComplete="off"
            before={<CalendarIcon className="tw-calculator__icon" />}
            inputMode="decimal"
            name="term"
          />
          <Input
            aria-label="financeInterest"
            autoComplete="off"
            before={<PercentIcon className="tw-calculator__icon" />}
            inputMode="numeric"
            name="interest"
            placeholder={t("calculator.placeholderInterest")}
          />
        </form>
        <footer className="tw-calculator__footer">
          <span className="tw-calculator__footer-text">Expected Payment</span>
          <Input
            aria-label="financePayment"
            before={<DollarIcon className="tw-calculator__icon--dollar" />}
            className="tw-calculator__input--results"
            inputClassName="text-right"
            placeholder="–"
            readOnly
            value={calculateAmmortizingLoan(form, state)}
          />
        </footer>
      </Dropdown>
    </Toggle>
  );
};

export const Calculator = memo(UncachedCalculator);

/**
 * @see docs/worklog/TW-897-sales-calculator-ring/rfc.md
 */
